import { type Accessor, createSignal, type Setter } from "solid-js";

export type StoredSignalStorage = "local" | "session";

export type CreateStoredSignalOptions<T> = {
	/** @default "local" */
	storage?: StoredSignalStorage;
	serialize?: (value: T) => string;
	/** Return `undefined` to fall back to the initial value. */
	deserialize?: (stored: string) => T | undefined;
};

function getStorageApi(kind: StoredSignalStorage): Storage | null {
	if (typeof window === "undefined") {
		return null;
	}
	try {
		return kind === "session" ? window.sessionStorage : window.localStorage;
	} catch {
		return null;
	}
}

function readStoredValue<T>(
	key: string,
	initialValue: T,
	options: CreateStoredSignalOptions<T>,
): T {
	const storage = getStorageApi(options.storage ?? "local");
	if (!storage) {
		return initialValue;
	}

	const deserialize =
		options.deserialize ??
		((stored: string) => {
			try {
				return JSON.parse(stored) as T;
			} catch {
				return undefined;
			}
		});

	try {
		const raw = storage.getItem(key);
		if (raw === null) {
			return initialValue;
		}
		const parsed = deserialize(raw);
		return parsed === undefined ? initialValue : parsed;
	} catch {
		return initialValue;
	}
}

function writeStoredValue<T>(
	key: string,
	value: T,
	options: CreateStoredSignalOptions<T>,
): void {
	const storage = getStorageApi(options.storage ?? "local");
	if (!storage) {
		return;
	}

	const serialize = options.serialize ?? ((v: T) => JSON.stringify(v));

	try {
		storage.setItem(key, serialize(value));
	} catch {
		// Ignore storage failures (private browsing, quota, blocked storage).
	}
}

/**
 * `createSignal` backed by `localStorage` or `sessionStorage`.
 *
 * Reads once on init; writes on every update. Uses `JSON.stringify` / `JSON.parse`
 * by default — pass `serialize` / `deserialize` for custom formats.
 */
export function createStoredSignal<T>(
	key: string,
	initialValue: T,
	options: CreateStoredSignalOptions<T> = {},
): [Accessor<T>, Setter<T>] {
	const [value, setValue] = createSignal<T>(
		readStoredValue(key, initialValue, options),
	);

	const setStoredValue = ((...args: Parameters<Setter<T>>) => {
		(setValue as Setter<T>)(...args);
		writeStoredValue(key, value(), options);
	}) as Setter<T>;

	return [value, setStoredValue];
}
